/**
 * Money.
 *
 * Integer cents on the wire (`*_cents`), never a float — and never a bare number on screen.
 * Every amount renders as `S$1,275.00`, because these figures are read next to each other in
 * tables and a missing separator or a lost trailing zero is how people mis-read a total.
 *
 * Amounts are GST-inclusive by definition (R-01/R-02); the forms that take a total say so.
 */

/**
 * The number, and the symbol, kept separate on purpose.
 *
 * `Intl.NumberFormat('en-SG', {currency: 'SGD'})` renders a bare `$` — correct in Singapore,
 * where `$` *is* the local currency, and wrong for a company that also handles other
 * currencies. Worse, where the symbol lands relative to a minus sign is an ICU detail that
 * differs between engines: the first attempt here post-processed the formatted string and
 * produced `-$24.00` for a negative adjustment, because the regex anchored at a `$` that was
 * no longer first.
 *
 * So the symbol is ours and only the digits are Intl's. `S$` cannot then be displaced by a
 * sign, a locale, or a browser's ICU build.
 */
const DIGITS = new Intl.NumberFormat('en-SG', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `S$1,275.00`. Negative amounts keep their sign — an adjustment may be one (D-53). */
export function formatCents(cents: number): string {
  return `${cents < 0 ? '-' : ''}S$${DIGITS.format(Math.abs(cents) / 100)}`;
}

/** `1,275.00` — for an input field, where the currency lives in the label. */
export function formatCentsPlain(cents: number): string {
  return DIGITS.format(cents / 100);
}

/**
 * Parse what someone typed into integer cents.
 *
 * Deliberately not `Math.round(parseFloat(value) * 100)`: `parseFloat('1180.10') * 100` is
 * 118009.99999999999, and rounding hides that only until it doesn't. Splitting on the decimal
 * point keeps the arithmetic in integers, where it belongs.
 *
 * Returns null for anything that is not a plain amount, so the caller can say so rather than
 * silently claiming S$0.
 */
export function parseAmountToCents(input: string): number | null {
  const cleaned = input.trim().replace(/^S?\$/i, '').replace(/,/g, '').replace(/\s/g, '');
  if (cleaned === '') return null;
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned)) return null;

  const [whole = '0', fraction = ''] = cleaned.split('.');
  if (whole === '' && fraction === '') return null;
  const cents = Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0') || '0');
  return Number.isSafeInteger(cents) ? cents : null;
}

/** `60%` — participation is a ratio 0–1 from the API, or null when there is no headcount. */
export function formatRatio(ratio: number | null): string {
  if (ratio === null) return '—';
  return `${Math.round(ratio * 100)}%`;
}
